import { Icon } from '@design/components/icon/Icon';
import { formatPercentage } from '@helpers/utils/numbers/numbers';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './TopCompetitors.module.scss';
import { CompetitorEarningsSnapshot, Guidance } from './utils';

export const Placeholder = () => <span className={styles.placeholder}>-</span>;

export const SignedPercentage = ({ value }: { value?: number }) => {
  if (value === undefined) {
    return <Placeholder />;
  }

  return (
    <span
      className={clsx({
        [styles.bullish]: value > 0,
        [styles.bearish]: value < 0,
      })}
    >
      {formatPercentage(value, {
        useOriginalValue: true,
        suffix: '%',
        addPlus: true,
      })}
    </span>
  );
};

export const GuidanceCell = ({ guidance }: { guidance?: Guidance }) => {
  const { t } = useTranslation(['earnings']);

  if (!guidance) {
    return <Placeholder />;
  }

  const isRaised = guidance === 'raised';

  return (
    <span
      className={clsx(styles.guidance, {
        [styles.bullish]: isRaised,
        [styles.bearish]: !isRaised,
      })}
    >
      <Icon
        iconName={isRaised ? 'GuidanceUp' : 'GuidanceDown'}
        size={12}
        fillColor="currentColor"
        aria-hidden
      />
      {t(
        isRaised
          ? 'earnings:peerComparison.topCompetitors.guidance.raised'
          : 'earnings:peerComparison.topCompetitors.guidance.lowered'
      )}
    </span>
  );
};

export const ReportingDateCell = ({
  reportingDate,
  hasReported,
}: Pick<CompetitorEarningsSnapshot, 'reportingDate' | 'hasReported'>) => {
  const { t } = useTranslation(['earnings']);

  if (!reportingDate) {
    return (
      <span className={styles.placeholder}>
        {t('earnings:peerComparison.topCompetitors.notAnnounced')}
      </span>
    );
  }

  return (
    <span className={styles.reportingDate}>
      {reportingDate}
      {hasReported === false && (
        <Icon
          iconName="SunRise"
          size={16}
          role="img"
          fillColor="var(--icon-primary)"
          aria-label={t(
            'earnings:peerComparison.topCompetitors.notReportedYet'
          )}
        />
      )}
    </span>
  );
};
